import React, { useState } from 'react';
import { FileDown, Copy, Check, Trash2 } from 'lucide-react';
import AIDetectionBadge from './AIDetectionBadge';

export default function ResultPanel({ result, lang, t, onClear }) {
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const A = t.app;

  if (!result) return null;

  const handleDownload = () => {
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = result.data;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const lineCount = result.data ? result.data.split('\n').length : 0;
  const charCount = result.data ? result.data.length : 0;
  const isRTL = result.type === 'translated_text' && lang !== 'en';
  const charLabel = lang === 'en' ? 'chars' : lang === 'fa' ? 'کاراکتر' : 'حرف';
  const lineLabel = lang === 'en' ? 'lines' : lang === 'fa' ? 'خط' : 'سطر';

  return (
    <div className="slide-down" style={{
      background: '#13131f',
      border: '1px solid rgba(0,212,170,0.25)',
      borderRadius: '14px',
      overflow: 'hidden',
      direction: lang !== 'en' ? 'rtl' : 'ltr',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(0,212,170,0.12), rgba(0,212,170,0.04))',
        borderBottom: '1px solid rgba(0,212,170,0.18)',
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ color: '#00d4aa', fontWeight: 700, fontSize: '15px' }}>
          ✅ {A.success}
        </span>
        <button
          onClick={onClear}
          style={{
            background: 'none', border: 'none', color: '#6b7280',
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            gap: '5px', fontSize: '13px', fontFamily: 'Tajawal, sans-serif',
          }}
        >
          <Trash2 size={14} /> {A.clear}
        </button>
      </div>

      <div style={{ padding: '20px' }}>
        {result.type === 'download' ? (
          <div>
            <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '14px' }}>
              📄 {result.filename}
            </p>
            <button
              onClick={handleDownload}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #00d4aa, #00b894)',
                border: 'none', borderRadius: '10px',
                color: 'white', fontFamily: 'Tajawal, sans-serif',
                fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(0,212,170,0.35)',
                transition: 'all 0.25s ease',
              }}
            >
              <FileDown size={20} /> {A.download}
            </button>
            <AIDetectionBadge detection={result.ai_detection} targetLang={result.targetLang} />
            {result.previewText && (
              <div style={{ marginTop: '14px' }}>
                <button
                  onClick={() => setShowPreview(v => !v)}
                  style={{
                    background: 'none', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '7px', color: '#9ca3af', cursor: 'pointer',
                    fontSize: '12px', padding: '5px 12px',
                    fontFamily: 'Tajawal, sans-serif',
                  }}
                >
                  {showPreview ? '▲ Hide text preview' : '▼ Show text preview'}
                </button>
                {showPreview && (
                  <pre style={{
                    marginTop: '10px', maxHeight: '320px', overflowY: 'auto',
                    background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '8px', padding: '14px',
                    fontFamily: "'Tajawal', sans-serif", fontSize: '12px',
                    color: '#d4d4d4', lineHeight: 1.6,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    direction: lang !== 'en' ? 'rtl' : 'ltr',
                  }}>
                    {result.previewText}
                  </pre>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ color: '#6b7280', fontSize: '12px' }}>
                {charCount.toLocaleString()} {charLabel} · {lineCount.toLocaleString()} {lineLabel}
              </span>
              <button
                onClick={handleCopy}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '7px 14px',
                  background: copied ? 'rgba(0,212,170,0.12)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${copied ? 'rgba(0,212,170,0.35)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '8px',
                  color: copied ? '#00d4aa' : '#9ca3af',
                  cursor: 'pointer', fontSize: '13px',
                  fontFamily: 'Tajawal, sans-serif',
                  transition: 'all 0.2s ease',
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? A.copied : A.copyCode}
              </button>
            </div>
            <pre style={{
              maxHeight: '420px', overflowY: 'auto',
              background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '8px', padding: '16px',
              fontFamily: result.type === 'latex_code' ? "'JetBrains Mono', monospace" : "'Tajawal', sans-serif",
              fontSize: '13px', color: '#d4d4d4', lineHeight: 1.65,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left',
            }}>
              {result.data}
            </pre>
            <AIDetectionBadge detection={result.ai_detection} targetLang={result.targetLang} />
          </div>
        )}
      </div>
    </div>
  );
}
